import { useEffect, useMemo, useState } from "react";
import { Button, Tab, Tabs } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import useCustomer from "../../../context/CustomerContext";
import { useAddCompanyDockMutation, useCreateCompanyProfileMutation, useDeleteCompanyProfileMutation, useEditCompanyDockMutation, useGetAllCompanyProfilesQuery, useUpdateCompanyProfileMutation } from "../../../features/admin/companyProfile/companyProfileSlice";
import { useGetAllDivisionsQuery } from "../../../features/admin/divisions/divisionSlice";
import Dialog from "../../designSystem/Dialog";
import errorHandler from "../../hooks/ErrorHanlder";
import ListLayout from "../../Shared/ListLayout";
import Wrapper from "../../Wrapper";
import AddressForm from "./AddressForm";
import BusinessInformation from "./BusinessInformation";
import ButtonActions from "./ButtonActions";
import { billingToDto, companyProfileToDto, defaultNestedValues, defaultValues, formDockToDto, receivableToDto } from "./companyProfileDtos";
import DivisionRateCard from "./DivisionRateCard";
import DockList from "./DockList";

const CompanyProfile = () => {
  const [mode, setMode] = useState("ADD");
  const [key, setKey] = useState("COMPANY_PROFILE");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [createCompanyProfile] = useCreateCompanyProfileMutation();
  const [deleteCompanyProfile] = useDeleteCompanyProfileMutation();
  const [updateCompanyProfile] = useUpdateCompanyProfileMutation();
  const [addCompanyDock] = useAddCompanyDockMutation();
  const [editCompanyDock] = useEditCompanyDockMutation();
  const [divisions, setDivisions] = useState([]);
  const { data: profilesData } = useGetAllCompanyProfilesQuery();
  const { data: divisionList, isLoading: divisionListIsLoading } = useGetAllDivisionsQuery();
  const { handleAccount, companyProfile, setCompanyProfileID } = useCustomer();
  const isProfileActive = companyProfile != null
  const isReadOnly = mode === "VIEW"
  const conditionOperation = mode === "VIEW" || mode === "EDIT"
  const rateCardId = companyProfile?.rateCardDetails?.mLContractInfos[0]?.rateCardId;


  const companyProfilesList = useMemo(
    () =>
      profilesData?.data?.map((item, i) => ({
        ...item,
        id: String(i + 1).toString(),
      })) ?? [],
    [profilesData]
  );

  useEffect(() => {
    if (!profilesData || divisionListIsLoading) return;

    const selectedCompanyDivision = companyProfile?.division;

    const usedByOthers = new Set(
      profilesData.data
        .map(p => p.division)
        .filter(div => div && div !== selectedCompanyDivision)
    );

    const availableDivisions = divisionList.data.filter(
      d => !usedByOthers.has(d.divisionName)
    );

    setDivisions(availableDivisions);

    return () => setDivisions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesData, divisionListIsLoading, companyProfile?.division]);

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });
  const { reset, setValue, getValues, trigger } = methods;

  const onSubmit = async (values) => {
    const billingSameAddress = values.billing.sameAsCompanyProfile
    const receivableSameAddress = values.receivable.sameAsCompanyProfile

    const crossDockMap = (values.docks || []).map((dock) => ({
      isPrimary: dock.isPrimary,
      dockID: dock.id,
      dockName: dock.dockName,
      dockCode: dock.dockCode ?? 0,
      dockStreetNo: dock.streetNo,
      dockStreetName: dock.streetName,
      dockCity: dock.city[0]?.name,
      dockProvince: dock.provinceList?.data?.data?.find((p) => p.provineStateID === Number(dock.province))?.provineSateName,
      dockCountry: dock.country === 2 ? "USA" : "CAN",
      dockPostalCode: dock.postalCode,
    }));
    const companyProfileValues = {
      companyName: values.companyName,
      streetNo: values.streetNo,
      streetName: values.streetName,
      city: values.companyProfile.cityName ?? values.companyProfile.city[0]?.name,
      province: values.companyProfile.provinceName ?? values.companyProfile.provinceList?.data?.data[0]?.provineSateName,
      country: values.companyProfile.country === 2 ? "USA" : "CAN",
      postalCode: values.postalCode,
      email: values.email,
      phoneNumber: values.phoneNumber,
      faxNumber: values.faxNumber,
      businessNumber: values.businessNumber,
      taxNumber: values.taxNumber,
      division: values.division,
      website: values.website,
      icon: values.icon,
      divisionPrefix: values.divisionPrefix,
      isBillingSameAsMailing: values.billing.sameAsCompanyProfile,
      billingStreetNo: values.billing.streetNo,
      billingStreetName: values.billing.streetName,
      billingCity: billingSameAddress ? values.companyProfile.city[0]?.name : values.billing.city[0]?.name,
      billingProvince: billingSameAddress ? values.companyProfile.provinceList?.data?.data?.find((p) => p.provineStateID === Number(values.companyProfile.province))?.provineSateName : values.billing.provinceList?.data?.data?.find((p) => p.provineStateID === Number(values.billing.province))?.provineSateName,
      billingCountry: billingSameAddress ? values.companyProfile.country === 2 ? "USA" : "CAN" : values.billing.country === 2 ? "USA" : "CAN",
      billingPostalCode: values.billing.postalCode,
      isReceivableSameAsMailing: values.receivable.sameAsCompanyProfile,
      receivableStreetNo: values.receivable.streetNo,
      receivableStreetName: values.receivable.streetName,
      receivableCity: receivableSameAddress ? values.companyProfile.city[0]?.name : values.receivable.city[0]?.name,
      receivableProvince: receivableSameAddress ? values.companyProfile.provinceList?.data?.data?.find((p) => p.provineStateID === Number(values.companyProfile.province))?.provineSateName : values.receivable.provinceList?.data?.data?.find((p) => p.provineStateID === Number(values.receivable.province))?.provineSateName,
      receivableCountry: receivableSameAddress ? values.companyProfile.country === 2 ? "USA" : "CAN" : values.receivable.country === 2 ? "USA" : "CAN",
      receivablePostalCode: values.receivable.postalCode,
      unit: values.unit,
      contact: values.contact,
      phoneExt: values.phoneExt,
      openTime: values.openTime,
      closeTime: values.closeTime,
      locationNotes: values?.locationNotes,
      crossDocks: crossDockMap
    }
    try {
      if (conditionOperation) {
        await updateCompanyProfile({ DTO: companyProfileValues, CompanyProfileID: companyProfile?.companyProfileID }).unwrap();
        toast.success("Company profile updated successfully", { position: "top-center" });
      } else {
        await createCompanyProfile(companyProfileValues).unwrap();
        toast.success("Company profile created successfully", { position: "top-center" });
      }
      onClickCancel()
    } catch (error) {
      errorHandler(error)
    }
  };
  const onSubmitDock = async () => {
    const isValid = await trigger([
      "dockForm.dockName",
      "dockForm.streetNo",
      "dockForm.streetName",
      "dockForm.city",
      "dockForm.province",
      "dockForm.country",
      "dockForm.postalCode",
    ]);
    if (!isValid) return;

    const dock = getValues("dockForm");
    const allDocks = getValues("docks") || [];

    const updatedDocks = dock.dockID
      ? allDocks.map(d => d.dockID === dock.dockID ? { ...d, ...dock } : d)
      : [
        ...allDocks,
        {
          ...dock,
          dockProvince: dock.provinceList?.data?.data?.find((p) => p.provineStateID === Number(dock.province))?.provineSateName,
          dockID: Math.floor(Math.random() * 2_147_483_647) + 1,
          isPrimary: allDocks.length === 0
        }
      ];

    if (isProfileActive) {
      try {
        const body = {
          dockID: dock.dockID,
          dockName: dock.dockName,
          dockCode: dock.dockCode ?? 0,
          dockStreetNo: dock.streetNo,
          dockStreetName: dock.streetName,
          dockCity: dock.city[0]?.name,
          dockProvince: dock.provinceList?.data?.data?.find((p) => p.provineStateID === Number(dock.province))?.provineSateName,
          dockCountry: dock.country === 2 ? "USA" : "CAN",
          dockPostalCode: dock.postalCode,
          isPrimary: allDocks.length === 0
        };

        if (dock.dockID) {
          await editCompanyDock({ ...body, CompanyProfileID: companyProfile.companyProfileID }).unwrap();
          toast.success("Dock updated successfully", { position: "top-center" });
        } else {
          await addCompanyDock({ ...body, CompanyProfileID: companyProfile.companyProfileID }).unwrap();
          toast.success("Dock added successfully", { position: "top-center" });
        }
      } catch (err) {
        return errorHandler(err);
      }
    }

    if (!isProfileActive) {
      setValue("docks", updatedDocks);
    }
    setValue("dockForm", {
      ...defaultNestedValues,
      dockName: "",
      isPrimary: false
    });
    setValue("dockForm.city", []);
  };

  const handleItemSelect = (item) => {
    setMode("VIEW");
    handleAccount(null, item.companyProfileID);
  }
  const billingDto = useMemo(() => billingToDto({ companyProfile }), [companyProfile]);
  const receivableDto = useMemo(() => receivableToDto({ companyProfile }), [companyProfile]);
  const profileDto = useMemo(() => companyProfileToDto({ companyProfile }), [companyProfile]);
  const docksDto = useMemo(
    () => (companyProfile?.crossDocks || []).map(formDockToDto),
    [companyProfile?.crossDocks]
  );

  useEffect(() => {
    if (!conditionOperation) return;

    reset({
      companyName: companyProfile?.companyName,
      streetNo: companyProfile?.streetNo,
      streetName: companyProfile?.streetName,
      postalCode: companyProfile?.postalCode,
      email: companyProfile?.email,
      phoneNumber: companyProfile?.phoneNumber,
      faxNumber: companyProfile?.faxNumber,
      businessNumber: companyProfile?.businessNumber,
      taxNumber: companyProfile?.taxNumber,
      division: companyProfile?.division,
      website: companyProfile?.website,
      divisionPrefix: companyProfile?.divisionPrefix,
      icon: companyProfile?.icon,
      companyProfile: profileDto,
      billing: billingDto,
      receivable: receivableDto,
      docks: docksDto,
      unit: companyProfile?.unit,
      contact: companyProfile?.contact,
      phoneExt: companyProfile?.phoneExt,
      openTime: companyProfile?.openTime,
      closeTime: companyProfile?.closeTime,
      locationNotes: companyProfile.locationNotes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfile, reset]);

  const onClickCancel = () => {
    reset(defaultValues)
    setMode("ADD");
    setSelectedIndex(null);
    setCompanyProfileID(null);
  }
  const deleteHandler = async () => {
    try {
      await deleteCompanyProfile(companyProfile?.companyProfileID).unwrap();
      reset(defaultValues);
      setMode("ADD");
      setCompanyProfileID(null);
      toast.success("Company Profile deleted Successfully", { position: "top-center" });
      setIsOpen(false);
    } catch (error) {
      errorHandler(error)
    }
  }

  return (
    <Wrapper>
      <ListLayout
        items={companyProfilesList}
        isLoading={false}
        hideOnFavorite={true}
        onItemSelect={handleItemSelect}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
      >
        <Dialog title="Delete Company profile" show={isOpen} onClose={() => setIsOpen(false)} description={"Are you sure you want to Delete Company profile"} onSubmit={deleteHandler} />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3> {companyProfile?.companyName ?? "Company Profile"} </h3>
          {isProfileActive && key === "COMPANY_PROFILE" ? <Button variant="outline-danger" onClick={() => setIsOpen(true)}>Delete Company Profile</Button> : null}
        </div>
        <Tabs id="controlled-tab-example" activeKey={key} onSelect={(k) => {
          setKey(k)
          setCompanyProfileID(companyProfile.companyProfileID)
        }} className="mb-3">
          <Tab eventKey="COMPANY_PROFILE" title="Company Profile Info">
            <FormProvider {...methods}>
              <ButtonActions onSubmit={onSubmit} mode={mode} setMode={setMode} onClickCancel={onClickCancel} />
              <BusinessInformation divisions={divisions} readOnly={isReadOnly} />
              <AddressForm title="Billing Address" formNamespace="billing" readOnly={isReadOnly} />
              <AddressForm title="Receivable" formNamespace="receivable" readOnly={isReadOnly} />
              <AddressForm title="Dock" formNamespace="dockForm" readOnly={false} handleAddDock={onSubmitDock} >
                <DockList shouldCallBackend={isProfileActive} editCompanyDock={editCompanyDock} />
              </AddressForm>
            </FormProvider>
          </Tab>
          {conditionOperation ? <Tab eventKey="RATE_CARD" title="Rate Card">
            <DivisionRateCard CompanyProfileID={companyProfile?.companyProfileID ?? 0} hasRateCard={rateCardId} />
          </Tab> : null}
        </Tabs>
      </ListLayout>
    </Wrapper >
  );
};
export default CompanyProfile;
